import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { backendApi } from '../lib/backendApi';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2 } from 'lucide-react';

const ResetPasswordPage = () => {
  const { api, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetToken = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const presetEmail = useMemo(() => searchParams.get('email') || '', [searchParams]);
  const [activeTab, setActiveTab] = useState(presetToken ? 'link' : 'code');
  const [token, setToken] = useState(presetToken);
  const [email, setEmail] = useState(presetEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const payload = activeTab === 'link'
        ? { token, new_password: newPassword }
        : { email, code, new_password: newPassword };
      const response = await backendApi.auth.resetPassword(api, payload);
      setMessage(response.data?.message || 'Password reset successful');
      setNewPassword('');
      setCode('');
      if (activeTab === 'link') {
        setToken('');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = async (e) => {
    e.preventDefault();
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <Card className="border-border/60 bg-card/75 shadow-xl backdrop-blur">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Reset Password</CardTitle>
            <CardDescription>
              Reset your password using either the email link token or the 6-digit code from your inbox.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="link">Use Link</TabsTrigger>
                <TabsTrigger value="code">Use Code</TabsTrigger>
              </TabsList>

              <form onSubmit={submit} className="space-y-4 pt-4">
                {error ? <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert> : null}
                {message ? <Alert className="border-border/60 bg-background/70"><AlertDescription>{message}</AlertDescription></Alert> : null}

                <TabsContent value="link" className="space-y-4 mt-0">
                  <div className="space-y-2">
                    <Label htmlFor="token">Reset Token</Label>
                    <Input
                      id="token"
                      required={activeTab === 'link'}
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      placeholder="Paste the token from your reset link"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="code" className="space-y-4 mt-0">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      required={activeTab === 'code'}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="code">6-digit Reset Code</Label>
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      required={activeTab === 'code'}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      className="tracking-[0.25em] font-mono"
                    />
                  </div>
                </TabsContent>

                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <PasswordInput
                    id="new-password"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Resetting...</> : 'Reset Password'}
                </Button>
              </form>
            </Tabs>

            <p className="text-sm text-muted-foreground mt-4 text-center">
              Back to <Link className="text-primary hover:underline" to="/login" onClick={handleGoToLogin}>Login</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
